import React from "react";
import { Badge } from "../components/ui/badge";
import { Calendar, MessageSquare, Clock } from "lucide-react";
import { LOCALES } from "../i18n";

const priorityStyles = {
    high: "bg-red-50 text-red-700 border-red-200",
    medium: "bg-orange-50 text-orange-700 border-orange-200",
    low: "bg-stone-50 text-stone-700 border-stone-200",
};

const CURRENCY_SYMBOL = { TRY: "₺", EUR: "€", USD: "$" };

function relTime(iso, lang) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString(LOCALES[lang] || "en-US", { dateStyle: "short", timeStyle: "short" });
}

function shortDate(iso, lang) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString(LOCALES[lang] || "en-US", { month: "short", day: "numeric" });
}

function formatPrice(price, currency) {
    if (price == null || price === "") return null;
    const sym = CURRENCY_SYMBOL[currency] || "";
    const n = Number(price);
    if (!Number.isFinite(n)) return null;
    const formatted = n.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    return `${sym}${formatted}`;
}

export default function TaskCard({ task, users, onOpen, lang, draggable = false, onDragStart }) {
    const assignees = (task.assignee_ids || [])
        .map((id) => users.find((u) => u.id === id))
        .filter(Boolean);

    const overdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== "completed";

    return (
        <div
            data-testid={`task-card-${task.id}`}
            onClick={onOpen}
            draggable={draggable}
            onDragStart={onDragStart}
            className="group flex flex-col gap-2 rounded-md border border-neutral-200 bg-white p-3 shadow-sm hover:shadow-md hover:border-neutral-300 transition-all cursor-pointer animate-fade-in"
        >
            <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold text-neutral-900 leading-snug flex-1 line-clamp-2">
                    {task.title}
                </h3>
                <Badge variant="outline" className={`shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0 h-5 ${priorityStyles[task.priority] || priorityStyles.medium}`}>
                    {task.priority}
                </Badge>
            </div>

            {task.description && (
                <p className="text-xs text-neutral-600 line-clamp-2">{task.description}</p>
            )}

            {/* Price */}
            {formatPrice(task.price, task.currency) && (
                <div className="inline-flex items-center self-start px-1.5 py-0.5 rounded bg-neutral-900 text-white text-[10px] font-mono font-semibold" data-testid={`task-price-${task.id}`}>
                    {formatPrice(task.price, task.currency)}
                </div>
            )}

            {/* Created at meta */}
            <div className="flex items-center gap-1 text-[10px] text-neutral-400" data-testid={`task-created-${task.id}`}>
                <Clock className="w-2.5 h-2.5" />
                <span>{relTime(task.created_at, lang)}</span>
            </div>

            <div className="flex items-center justify-between mt-0.5 pt-2 border-t border-neutral-100">
                <div className="flex items-center gap-3 text-[11px] text-neutral-500">
                    {task.deadline && (
                        <span className={`flex items-center gap-1 ${overdue ? "text-red-600 font-medium" : ""}`} data-testid={`task-deadline-${task.id}`}>
                            <Calendar className="w-3 h-3" />
                            {shortDate(task.deadline, lang)}
                        </span>
                    )}
                    {task.comments?.length > 0 && (
                        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{task.comments.length}</span>
                    )}
                </div>
                {assignees.length > 0 && (
                    <div className="flex -space-x-1.5">
                        {assignees.slice(0, 3).map((u) => (
                            <div key={u.id}
                                title={u.name}
                                className="w-6 h-6 rounded-full bg-neutral-900 text-white text-[10px] font-semibold flex items-center justify-center border-2 border-white">
                                {u.name?.slice(0, 2)?.toUpperCase()}
                            </div>
                        ))}
                        {assignees.length > 3 && (
                            <div className="w-6 h-6 rounded-full bg-neutral-100 text-neutral-700 text-[10px] font-semibold flex items-center justify-center border-2 border-white">
                                +{assignees.length - 3}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
